import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { advance } from '@onward-delivery/core';
import { Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor } from './blocks';
import { formatInTimeZone } from 'date-fns-tz';

// Service-call variant of the pickup step: one "Out for Service" action, same events/flags as a normal pickup.
export default function OutForService({ isDesktop, isLoading, before, after, order, callbacks, tz, readOnly, lastEvent }) {
    const STEPS = useMemo(() => {
        return [
            {
                description: `Mark order as 'Out for Service'`,
                complete: ({ order, after }) => !!after?.pickup_date && !!order.ltl_pickup_completed,
                ...(!readOnly
                    ? {
                          cta: 'Out for Service',
                          callback: ({ order, after } = {}) => {
                              const NOW = new Date();
                              const prompt = isDesktop ? (
                                  <Grid direction="row" container>
                                      <Body1>Mark this service order as 'Out for Service'?</Body1>
                                  </Grid>
                              ) : (
                                  <Grid direction="row" container>
                                      <Header1>Out for Service</Header1>
                                  </Grid>
                              );

                              const callback = (orderUpdates = {}) => {
                                  const pickupTime = orderUpdates?.ltl_pickup_completed || NOW.toISOString();
                                  const orderPatch = {
                                      order_status: 'inProgress',
                                      loading_completed: pickupTime,
                                      ltl_pickup_completed: pickupTime,
                                      ...orderUpdates,
                                  };

                                  // Edit: the order is already at CD_COMPLETE so advance() returns no events;
                                  // restamp the existing confirming event directly (mirrors PickedUp).
                                  if (after?.event_id) {
                                      return callbacks.upsertEvent({
                                          event: {
                                              event_id: after.event_id,
                                              action: after.action,
                                              order_id: order.order_id,
                                              pickup_date: pickupTime,
                                          },
                                          orderId: order.order_id,
                                          order: orderPatch,
                                      });
                                  }

                                  const events = advance(order, order.event_state, 'CD_COMPLETE', {});
                                  if (events.length) {
                                      events[events.length - 1] = {
                                          ...events[events.length - 1],
                                          pickup_date: pickupTime,
                                      };
                                  }

                                  return callbacks.upsertEvent({ events, orderId: order.order_id, order: orderPatch });
                              };

                              callbacks.openChecklist(
                                  {
                                      title: 'Out for Service',
                                      content: prompt,
                                      documents: [],
                                      signatures: [],
                                      timestamps: [
                                          { field: 'ltl_pickup_completed', name: 'Out for Service Time', required: true },
                                      ],
                                  },
                                  callback
                              );
                          },
                      }
                    : {}),
            },
        ];
    }, [isDesktop, order, readOnly, callbacks]);

    const isComplete = useMemo(() => STEPS.map((step) => step.complete({ order, before, after })), [
        STEPS,
        order,
        before,
        after,
    ]);

    const overrideComplete = useMemo(() => {
        return (
            !!after &&
            (['inProgress', 'routedInProgress', 'complete', 'routedComplete'].includes(order.order_status) ||
                lastEvent?.event_id !== after?.event_id)
        );
    }, [after, order, lastEvent]);

    const inProgressIdx = useMemo(() => isComplete.findIndex((x) => !x), [isComplete]);
    const allComplete = isComplete.every((x) => x);
    const inProgress = !!before && !(allComplete || overrideComplete);

    return (
        <Step
            isDesktop={isDesktop}
            isComplete={overrideComplete || allComplete}
            inProgress={inProgress}
            label={() => (
                <Grid
                    direction="row"
                    container
                    css={css`
                        align-items: center;
                    `}
                >
                    <Grid
                        direction="column"
                        css={css`
                            margin-right: 8px;
                        `}
                    >
                        <Header1>Out for Service</Header1>
                    </Grid>
                    {after?.pickup_date ? (
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            <Tag>
                                <Body1
                                    css={css`
                                        margin-right: 20px;
                                    `}
                                >{`Out for Service: ${formatInTimeZone(
                                    after.pickup_date,
                                    tz,
                                    'EEE, MMM dd, yyyy h:mm a zzz'
                                )}`}</Body1>
                                {!readOnly && (
                                    <Anchor onClick={() => STEPS[0]?.callback({ order, before, after })}>
                                        <Header1>Edit</Header1>
                                    </Anchor>
                                )}
                            </Tag>
                        </Grid>
                    ) : null}
                </Grid>
            )}
        >
            {() => (
                <>
                    {STEPS.map(({ description, cta, callback }, idx) => {
                        const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                        return (
                            <SubStep
                                key={idx}
                                isComplete={isComplete[idx]}
                                inProgress={inProgressIdx === idx}
                                isLoading={isLoading}
                                isLast={idx === STEPS.length - 1}
                                description={description}
                                cta={cta}
                                secondaryCta={null}
                                callback={callback ? () => callback({ before, after, order }) : null}
                                secondaryCallback={null}
                            />
                        );
                    })}
                </>
            )}
        </Step>
    );
}
